/**
 * O pórtico e a nossa visão, lado a lado (`SC-06a`, ADR-095).
 *
 * O pórtico lê a placa e conta os eixos; a `ia-placa` lê as fotos. São duas testemunhas da mesma
 * passagem, e esta função só as **põe lado a lado** com o veredito que o servidor já deu
 * (`placa_confere`, `eixos_conferem`): a regra mora no domínio, não aqui. Nada na tela aprova
 * por conta disso — quem decide é a pessoa (ADR-008).
 *
 * **Falta de leitura não é divergência.** Sem a nossa placa (a visão não leu), a linha diz
 * "sem leitura nossa" em cinza, e não "diverge" em vermelho: mostrar vermelho onde só falta dado
 * faria o operador desconfiar do pórtico à toa — ou parar de confiar no vermelho.
 */
import type { DetalheDoTransito } from "../api/tipos";
import type { Significado } from "../componentes/tinta";

export type Concordancia = "CONFERE" | "QUASE" | "DIVERGE" | "INCOMPARAVEL";

export interface LinhaDoPortico {
  chave: "placa" | "eixos" | "categoria";
  rotulo: string;
  nossa: string;
  portico: string;
  /** Nula: a linha é só informação (a categoria é de uma tabela que a nossa não usa). */
  situacao: Concordancia | null;
}

export interface ComparacaoDoPortico {
  linhas: LinhaDoPortico[];
  resumo: { texto: string; cor: Significado };
}

const SEM = "—";

function percentual(confianca: number | null): string {
  return confianca == null ? "" : ` (${Math.round(confianca * 100)}%)`;
}

export function textoDaSituacao(situacao: Concordancia): string {
  switch (situacao) {
    case "CONFERE":
      return "confere";
    case "QUASE":
      return "quase — confira a foto";
    case "DIVERGE":
      return "diverge";
    case "INCOMPARAVEL":
      return "sem leitura nossa";
  }
}

export function corDaSituacao(situacao: Concordancia | null): Significado {
  switch (situacao) {
    case "CONFERE":
      return "verde";
    case "QUASE":
      return "amarelo";
    case "DIVERGE":
      return "vermelho";
    default:
      return "cinza";
  }
}

/** A comparação do detalhe, ou `null` quando o pórtico não mandou leitura nenhuma. */
export function comparacaoDoPortico(
  detalhe: Pick<
    DetalheDoTransito,
    "placa" | "eixos_detectados" | "leitura_do_portico"
  >,
): ComparacaoDoPortico | null {
  const l = detalhe.leitura_do_portico;
  if (!l) return null;

  const linhas: LinhaDoPortico[] = [];
  if (l.placa != null) {
    linhas.push({
      chave: "placa",
      rotulo: "Placa",
      nossa: detalhe.placa ?? SEM,
      portico: `${l.placa}${percentual(l.confianca)}`,
      situacao: l.placa_confere,
    });
  }
  if (l.eixos != null) {
    linhas.push({
      chave: "eixos",
      rotulo: "Eixos",
      nossa:
        detalhe.eixos_detectados == null
          ? SEM
          : String(detalhe.eixos_detectados),
      portico: String(l.eixos),
      situacao: l.eixos_conferem,
    });
  }
  if (l.categoria != null) {
    linhas.push({
      chave: "categoria",
      rotulo: "Categoria (tabela do pórtico)",
      nossa: SEM,
      portico: String(l.categoria),
      situacao: null,
    });
  }

  const comparaveis = linhas
    .map((x) => x.situacao)
    .filter((s): s is Concordancia => s != null && s !== "INCOMPARAVEL");
  let resumo: ComparacaoDoPortico["resumo"];
  if (comparaveis.includes("DIVERGE")) {
    resumo = { texto: "O pórtico e a nossa leitura divergem", cor: "vermelho" };
  } else if (comparaveis.includes("QUASE")) {
    resumo = {
      texto: "Quase iguais — confira a placa na foto",
      cor: "amarelo",
    };
  } else if (comparaveis.length > 0) {
    resumo = { texto: "O pórtico e a nossa leitura conferem", cor: "verde" };
  } else {
    resumo = { texto: "Ainda sem leitura nossa para comparar", cor: "cinza" };
  }
  return { linhas, resumo };
}
